import { Flame, Droplets, ShieldCheck, Scale, Users, CloudRain, Sparkles, Handshake, MessagesSquare, BadgeCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  useSeoMeta, LandingHero, CoverageGrid, WhyUsSection,
  TestimonialsSection, FaqSection, Breadcrumb,
} from "../lib/landingShared";

const HERO_IMG = "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700";

const COVERAGE = [
  { icon: Flame,       title: "Incendio ed esplosione",  desc: "Danni alla struttura e al contenuto causati da incendio, scoppio o fulmine." },
  { icon: Droplets,    title: "Danni da acqua",          desc: "Rotture impianti, infiltrazioni e spese di ricerca del guasto." },
  { icon: ShieldCheck, title: "Furto e rapina",          desc: "Contenuto, scasso, atti vandalici e danni da effrazione." },
  { icon: Scale,       title: "Responsabilità civile",   desc: "Danni involontari causati a terzi dalla famiglia o dall'immobile." },
  { icon: Users,       title: "Famiglia e infortuni",    desc: "Tutela legale, infortuni domestici e assistenza in caso di emergenza." },
  { icon: CloudRain,   title: "Eventi atmosferici",      desc: "Grandine, vento, alluvione e, in opzione, eventi catastrofali." },
];

const WHY = [
  { icon: Sparkles,       title: "Più compagnie a confronto", desc: "Confrontiamo le condizioni delle principali compagnie e proponiamo la soluzione più equilibrata." },
  { icon: Handshake,      title: "Consulenza dedicata",       desc: "Partiamo dalla tua casa: tipologia, valore, abitudini della famiglia." },
  { icon: BadgeCheck,     title: "Chiarezza sulle clausole",  desc: "Massimali, franchigie ed esclusioni spiegate prima della firma." },
  { icon: MessagesSquare, title: "Un referente vero",         desc: "Una persona di riferimento in agenzia, anche dopo la sottoscrizione." },
];

const FAQS = [
  { q: "Cosa copre una polizza casa?", a: "Una polizza casa può tutelare l'immobile e il contenuto da una serie di rischi: incendio ed esplosione, danni da acqua, furto e rapina, atti vandalici, eventi atmosferici, responsabilità civile della famiglia, infortuni domestici, assistenza e tutela legale. La combinazione esatta delle garanzie viene definita insieme al cliente in fase di consulenza, in modo da non pagare ciò che non serve." },
  { q: "È possibile personalizzare la copertura?", a: "Sì. La polizza casa è modulare: si parte da un nucleo base (di solito incendio e responsabilità civile) e si aggiungono solo le garanzie utili al tuo caso — proprietario o inquilino, appartamento o villa, presenza di antifurto, oggetti di valore, terreni di pertinenza, eccetera. La rata varia di conseguenza." },
  { q: "Come funziona la franchigia?", a: "La franchigia è la parte di danno che resta a tuo carico in caso di sinistro. Serve a tenere il premio annuo più basso, scoraggiando le piccole richieste di indennizzo. Si può scegliere un valore più alto (rata più bassa) o più basso (rata più alta, ma copertura più reattiva). Te ne illustriamo gli effetti reali prima della firma." },
  { q: "Quanto costa assicurare una casa?", a: "Il premio dipende da diversi fattori: metratura, tipologia (appartamento, villa, indipendente), zona, anno di costruzione, presenza di sistemi di sicurezza, valore del contenuto e garanzie scelte. In media, una polizza casa completa per un appartamento di 90 mq parte da circa 200-350 € all'anno. Richiedendo una consulenza ti forniamo un preventivo personalizzato senza impegno." },
];

const TRUST = [
  { value: "1958", label: "Anno di fondazione" },
  { value: "3",    label: "Generazioni di consulenza" },
  { value: "24h",  label: "Risposta alla richiesta" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
};

export default function Casa() {
  useSeoMeta({
    title: "Assicurazione Casa Milano | Da Pra Assicurazioni",
    description: "Assicurazione casa con consulenza personalizzata: incendio, furto, danni da acqua, responsabilità civile, eventi atmosferici. Agenzia assicurativa plurimandataria dal 1958.",
    keywords: "assicurazione casa, polizza casa, protezione abitazione, polizza incendio casa, assicurazione abitazione, polizza famiglia",
    canonical: "https://dapraassicurazioni.it/assicurazione-casa",
    jsonLd: JSON_LD, jsonLdId: "ld-casa",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Breadcrumb current={{ label: "Assicurazione Casa", href: "/assicurazione-casa" }} />

      <LandingHero
        eyebrow="Polizza Casa · Dal 1958"
        title={<>La tua casa, <span className="italic">protetta come merita.</span></>}
        description="Una polizza pensata sulla tua abitazione e sulla tua famiglia. Scegliamo insieme cosa coprire — struttura, contenuto, responsabilità — confrontando le proposte delle compagnie con cui collaboriamo da tre generazioni."
        imageSrc={HERO_IMG}
        imageAlt="Assicurazione casa — Da Pra Assicurazioni"
        ctaPrimaryLabel="Richiedi una consulenza"
        ctaPrimaryHref="/preventivo?type=casa"
        trustItems={TRUST}
      />

      <CoverageGrid
        eyebrow="Garanzie disponibili"
        heading="Cosa puoi proteggere."
        intro="Componi la polizza partendo dalle garanzie che contano davvero per la tua casa."
        items={COVERAGE}
      />

      <WhyUsSection
        eyebrow="Il nostro approccio"
        heading="Consulenza vera, prima del preventivo."
        body={[
          "Da tre generazioni accompagniamo famiglie e proprietari nella scelta della polizza casa.",
          "Iniziamo sempre con un colloquio: tipologia di immobile, valore, abitudini della famiglia. Solo dopo confrontiamo le condizioni delle compagnie e ti proponiamo la soluzione più equilibrata.",
        ]}
        items={WHY}
      />

      <TestimonialsSection heading="La fiducia dei nostri clienti." />
      <FaqSection heading="Domande frequenti sulla polizza casa." faqs={FAQS} />

      <Footer />
    </div>
  );
}
